import { InvisibleButton } from '@/widgets/components/invisible-button';
import capitalize from 'lodash/capitalize';
import get from 'lodash/get';
import isBoolean from 'lodash/isBoolean';
import isString from 'lodash/isString';
import lowerCase from 'lodash/lowerCase';
import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import * as dataSelectors from '@/data/data-selectors';
import * as userSelectors from '@/user/user-selectors';
import { convertStringToPhoneNumber } from '@/utils/string-utils';
import { DocumentModal } from '@/widgets/components/document-modal';
import * as lc from './license-content-styles';

export function PhoneHOC(WrappedComponent) {
  const ReturnWrappedComponent = (props) => {
    const { content, ...other } = props;

    const valueToDisplay = useMemo(() => {
      if (!content) return null;
      return convertStringToPhoneNumber(content);
    }, [content]);

    /**
     * Use ...other here instead of ...props
     * Otherwise "content" from props will override the "valueToDisplay"
     */
    return <WrappedComponent content={valueToDisplay} {...other} />;
  };

  return ReturnWrappedComponent;
}

export function BooleanHOC(WrappedComponent) {
  const ReturnWrappedComponent = (props) => {
    const { content, allowNonBoolean, ...other } = props;

    const valueToDisplay = useMemo(() => {
      // For displaying either "Yes" or "No" for Secondary Dwelling as we receive a boolean
      if (isBoolean(content) || allowNonBoolean) return content ? 'Yes' : 'No';
      return null;
    }, [content]);

    return <WrappedComponent content={valueToDisplay} {...other} />;
  };

  return ReturnWrappedComponent;
}

export function MapHOC(WrappedComponent) {
  return function ReturnWrappedComponent(props) {
    const { map = {}, content: mapKey, ...other } = props;

    /**
     * Chain "capitalize" and "lowerCase", because if the key is in
     *  another case then "lowerCase" will convert it to letter case
     *  and then "capitalize" will capitalize the first letter of
     *  the string
     *
     * e.g.
     *  localContact -> Local contact
     */
    const valueToDisplay = useMemo(
      () => map?.[mapKey] || capitalize(lowerCase(mapKey)),
      [map, mapKey]
    );

    return <WrappedComponent content={valueToDisplay} {...other} />;
  };
}

export function TranslatedMapHOCFactory(
  translationKeyPath,
  defaultTranslations = {}
) {
  return function TranslatedMapHOC(WrappedComponent) {
    const MapComponent = MapHOC(WrappedComponent);

    return function ReturnWrappedComponent(props) {
      const translations = useSelector(dataSelectors.translations);

      const translationSubset = useMemo(() => {
        const extractedTranslations = translationKeyPath
          ? get(translations, translationKeyPath)
          : translations;

        return Object.keys(extractedTranslations).reduce(
          (acc, translationKey) => {
            const translation = extractedTranslations[translationKey]?.default;

            if (!translation) return acc;

            return { ...acc, [translationKey]: translation };
          },
          {}
        );
      }, [translations]);

      return (
        <MapComponent
          map={{ ...defaultTranslations, ...translationSubset }}
          {...props}
        />
      );
    };
  };
}
export const RentalStructureHOC = TranslatedMapHOCFactory('rentalStructure', {
  main_structure: 'Main Structure',
  adu: 'ADU',
  multifamily: 'Multifamily',
});

export function DefaultRenderer({ label, content, allowZero }) {
  if ((!content && (!allowZero || content !== 0)) || !label) return null;

  const valueToDisplay = isString(content) ? content.trim() : content;

  return (
    <lc.InfoBlock>
      <lc.InfoLabel>{label}</lc.InfoLabel>
      <lc.InfoData>{valueToDisplay}</lc.InfoData>
    </lc.InfoBlock>
  );
}

export function ImageRenderer(props) {
  const { fileList = [] } = props;

  const { viewDocuments } = useSelector(userSelectors.userRights);
  const [activeDocLink, setActiveDocLink] = useState(null);

  const userClickDoc = useCallback(
    (fileLink) => {
      if (viewDocuments) setActiveDocLink(fileLink);
    },
    [viewDocuments, setActiveDocLink]
  );

  if (!fileList.length) return null;

  return (
    <lc.InfoBlock>
      <lc.InfoLabel>
        {viewDocuments
          ? props.label || 'Verification Documents'
          : 'Unauthorized to view file(s)'}
      </lc.InfoLabel>

      {viewDocuments && (
        <span style={{ display: 'flex', flexDirection: 'column' }}>
          {fileList.map(({ fileName, digifyEmbedLink }) => (
            <InvisibleButton
              key={digifyEmbedLink}
              onClick={() => userClickDoc(digifyEmbedLink)}
            >
              <lc.InfoData isImage>{fileName}</lc.InfoData>
            </InvisibleButton>
          ))}
        </span>
      )}

      <DocumentModal
        show={Boolean(activeDocLink)}
        onHide={() => setActiveDocLink(null)}
        docLink={activeDocLink}
        title={props.propertyAddress}
        iframeTitle="Secure Document"
      />
    </lc.InfoBlock>
  );
}
